import React, { useEffect, useMemo, useState } from 'react';
import {
  Loading,
  PrimaryButton,
  SecondaryButton,
  SelectOption,
  useModal,
} from '@shape-ds/components';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import pdfMake from 'pdfmake/build/pdfmake';
import { useRertDate } from 'pages/Reports/Rert/RertDateProvider';
import { useAssetsByWorkspaceQuery } from 'queries/assets/queries';
import { Dayjs, dayjs } from 'lib';
import { usePDF } from 'react-to-pdf';
import { FormSelect } from 'ui/FormComponents/FormSelect';
import { FormInput } from 'ui/FormComponents';
import { useReportAssetsWithHealthQuery } from 'queries/report/queries';
import { deviationToPDF } from 'components/DeviationToPdf';
import { PDF_FONT_CONFIG } from '@shape/lighthouse-components';
import { HealthScoreEquipments } from 'components/DeviationToPdf/types';
import { getAllMonths } from '../../Selects/DateSelect';
import './style.scss';
import { useGlobalNotification } from 'hooks';
import { BaseAsset } from 'queries/assets/contract';
import { Pdf } from 'pages/Pdf/Pdf';
import { useFeatureFlag } from '@shape/feature-flag';
import { useIsFetching } from '@tanstack/react-query';

pdfMake.fonts = PDF_FONT_CONFIG;

type customSelectOption = SelectOption & {
  external_id: string | null;
};
interface PdfModalProps {
  equipmentOptions: customSelectOption[];
  asset?: { id: string; name: string };
}
type PdfModalForm = {
  assetId: string;
  date: Dayjs;
  documentName: string;
};

const vesselsCodeList: { [key: string]: string } = {
  MV15: '0149',
  MVFLU: '0152',
  MV18: '0201',
  MV20: '0212',
  MV22: '0262',
  MV23: '0280',
  MV24: '0355',
  MV26: '0381',
  MV27: '0397',
  MV29: '0428',
  MV30: '0478',
  MV31: '0468',
  MV32: '0479',
  MV33: '4006',
};

const DATE_FORMAT = 'MMM/YY';

export const getDocumentName = (date: Dayjs, assetName?: string) => {
  if (!assetName) return '';
  const validatedAssetName = vesselsCodeList[assetName] ?? assetName;
  const documentName = `${validatedAssetName}-MSB20-00ST-${date.format('MMYY')}`;
  return documentName;
};

function flat(array: BaseAsset[]) {
  let result: BaseAsset[] = [];
  array.forEach((a) => {
    result.push(a);
    if (Array.isArray(a.children)) {
      result = result.concat(flat(a.children));
    }
  });
  return result;
}

const PdfModal: React.FC<PdfModalProps> = ({ equipmentOptions, asset }) => {
  const { date: rertDate } = useRertDate();
  const isNewPdf = useFeatureFlag('new-pdf-rert');
  const { t } = useTranslation();
  const isPdfFetching =
    useIsFetching({
      predicate: (query) => query.queryKey.includes('PDF_LOADING'),
    }) > 0;

  const { closeModal } = useModal();
  const [reportData, setReportData] = useState<{
    assetId: string;
    start: Dayjs | undefined;
    end: Dayjs | undefined;
    name: string;
  }>({
    assetId: '',
    name: '',
    start: undefined,
    end: undefined,
  });
  const [isLoading, setIsLoading] = useState(false);

  const [externalIdReferences, setExternalIdReferences] = useState<Record<string, BaseAsset>>();
  const [equipmentOptionsWebId, setEquipmentOptionsWebId] = useState<SelectOption[] | undefined>();

  const formMethods = useForm<PdfModalForm>({
    defaultValues: {
      date: rertDate.format(DATE_FORMAT),
      assetId: asset?.id ?? '',
      documentName: getDocumentName(rertDate, asset?.name),
    },
  });
  const [watchAsset, date] = formMethods.watch(['assetId', 'date']);

  const { toPDF, targetRef } = usePDF();
  useEffect(() => {
    if (equipmentOptions) {
      const itens = equipmentOptions.map((option) => ({
        label: option.label,
        value: option.external_id ?? option.value,
      }));
      itens && setEquipmentOptionsWebId(itens);
    }
  }, [equipmentOptions]);
  const { displaySnackbar } = useGlobalNotification();
  const { data: treeData } = useAssetsByWorkspaceQuery();

  useEffect(() => {
    if (treeData?.items) {
      const flatTree = flat(treeData.items);
      const treeByExternalId = flatTree.reduce((acc, curr) => {
        if (curr.external_id) {
          acc[curr.external_id] = curr;
        }
        return acc;
      }, {} as Record<string, BaseAsset>);
      setExternalIdReferences(treeByExternalId);
    }
  }, [treeData]);

  const { data, isFetching } = useReportAssetsWithHealthQuery({
    assetId: reportData.assetId,
    start: reportData.start && reportData.start.format('DD-MM-YYYY HH:mm:ss'),
    end: reportData.end && reportData.end.format('DD-MM-YYYY HH:mm:ss'),
  });
  const downloadPdf = async (data: HealthScoreEquipments) => {
    if (reportData.start && reportData.end) {
      const dateRange: [Date, Date] = [reportData.start?.toDate(), reportData.end.toDate()];
      const pdf = await deviationToPDF(data, dateRange, reportData.name, externalIdReferences);
      pdfMake.createPdf(pdf).download(`${reportData.name}.pdf`);
    }

    setIsLoading(false);
    closeModal();
  };

  useEffect(() => {
    if (data?.systems?.length === 0) {
      displaySnackbar({
        message: t('reports:pdfModal.error'),
        type: 'error',
      });
    }
    if (data && (data.systems?.length ?? 0) > 0) {
      downloadPdf(data);
    }
  }, [data]);

  const dates = useMemo(getAllMonths, []);

  useEffect(() => {
    if (!formMethods.getFieldState('documentName').isDirty && equipmentOptionsWebId) {
      const assetName = (!isNewPdf ? equipmentOptionsWebId : equipmentOptions)?.find(
        (option) => option.value === watchAsset,
      )?.label;

      formMethods.setValue('documentName', getDocumentName(dayjs(date, DATE_FORMAT), assetName));
    }
  }, [watchAsset, date, equipmentOptionsWebId]);

  const onSubmit = (data: PdfModalForm) => {
    if (!isNewPdf) {
      const end_date = dayjs(data.date, DATE_FORMAT).set('date', 24).hour(23).minute(59).second(59);
      const start_date = dayjs(end_date)
        .set('date', 25)
        .hour(0)
        .minute(0)
        .second(0)
        .subtract(30, 'days');
      setIsLoading(true);
      return setReportData({
        assetId: data.assetId,
        start: start_date,
        end: end_date,
        name: data.documentName,
      });
    }
    targetRef.current.classList.remove('pdf-preview-zoom-out');
    toPDF({
      method: 'save',
      resolution: 1,
      filename: formMethods.getValues('documentName'),
      page: {
        margin: 0,
        format: 'A4',
        orientation: 'portrait',
      },
    });
    targetRef.current.classList.add('pdf-preview-zoom-out');
  };

  return (
    <FormProvider {...formMethods}>
      <form className="pdf-modal" onSubmit={formMethods.handleSubmit(onSubmit)}>
        <div className="selects-wrapper">
          <FormSelect
            name="assetId"
            label={t('reports:pdfModal.labels.vessel')}
            selectOptionData={isNewPdf ? equipmentOptions : equipmentOptionsWebId}
          />
          <FormSelect
            name="date"
            label={t('reports:pdfModal.labels.month')}
            selectOptionData={dates}
          />
        </div>
        <div className="selects-wrapper">
          <FormInput
            name="documentName"
            label={t('reports:pdfModal.labels.documentName')}
            isRequired
          />
        </div>

        <SecondaryButton
          onClick={(e) => {
            e.preventDefault();
            closeModal();
          }}
        >
          {t('reports:pdfModal.buttons.cancel')}
        </SecondaryButton>

        <PrimaryButton
          type="submit"
          disabled={!watchAsset || isLoading || isFetching || (isNewPdf && isPdfFetching)}
          startIcon={isLoading || isFetching ? <Loading size="15px" /> : undefined}
        >
          {t('reports:pdfModal.buttons.generate')}
        </PrimaryButton>
        {watchAsset && isNewPdf && (
          <div className="pdf-preview-wrapper">
            {isPdfFetching ? (
              <>{t('pdf:generatingPreview')}</>
            ) : (
              <div ref={targetRef} className={'pdf-preview pdf-preview-zoom-out'}>
                <Pdf
                  asset={{
                    id: watchAsset,
                    name:
                      equipmentOptions?.find((option) => option.value === watchAsset)?.label ?? '',
                  }}
                  date={dayjs(date, 'MMM/YY')}
                  assetsCategory={'RERT'}
                />
              </div>
            )}
          </div>
        )}
      </form>
    </FormProvider>
  );
};

export default PdfModal;
